/* Desktop filters for main-collection-emojibator. Re-renders the section via the
   Section Rendering API; mobile keeps the theme's original drawer driven by
   facets.js. Listeners are delegated on the custom element because its content
   is replaced on every render. */
if (!customElements.get('collection-emojibator')) {
  class CollectionEmojibator extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.requestId = 0;
      this.submitDesktop = this.debounce(() => this.applyForm(this.querySelector('[data-filter-form="desktop"]')), 300);

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('toggle', this.onToggle.bind(this), true);
      this.addEventListener('wheel', this.onPillsWheel.bind(this), { passive: false });
      this.addEventListener('pointerdown', this.onPillsPointerDown.bind(this));

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
    }

    connectedCallback() {
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('popstate', this.onPopState);
    }

    onChange(event) {
      if (event.target.closest('[data-filter-form="desktop"]')) this.submitDesktop();
    }

    onSubmit(event) {
      const form = event.target.closest('[data-filter-form]');
      if (!form) return;
      event.preventDefault();
      this.applyForm(form);
    }

    onClick(event) {
      /* A drag over the pills ends with a click on a pill; don't follow it */
      if (this.pillsDragged) {
        this.pillsDragged = false;
        if (event.target.closest('.ce-pills')) {
          event.preventDefault();
          return;
        }
      }

      const filterLink = event.target.closest('[data-filter-link]');
      if (filterLink) {
        event.preventDefault();
        this.render(new URL(filterLink.href, window.location.origin).searchParams.toString());
        return;
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;

      const openDropdown = event.target.closest('.ce-dropdown[open]');
      if (openDropdown) {
        openDropdown.removeAttribute('open');
        openDropdown.querySelector('summary').focus();
      }
    }

    /* Only one desktop dropdown open at a time */
    onToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;
      this.querySelectorAll('.ce-dropdown[open]').forEach((other) => {
        if (other !== details) other.removeAttribute('open');
      });
    }

    onDocumentClick(event) {
      this.querySelectorAll('.ce-dropdown[open]').forEach((details) => {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
    }

    /* Vertical mouse wheel scrolls the pills sideways; at either end the page
       scrolls as usual */
    onPillsWheel(event) {
      const pills = event.target.closest('.ce-pills');
      if (!pills || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      const maxScroll = pills.scrollWidth - pills.clientWidth;
      if (maxScroll <= 0) return;

      const atStart = pills.scrollLeft <= 0 && event.deltaY < 0;
      const atEnd = pills.scrollLeft >= maxScroll - 1 && event.deltaY > 0;
      if (atStart || atEnd) return;

      event.preventDefault();
      pills.scrollLeft += event.deltaY;
    }

    /* Drag to scroll with the mouse; touch already scrolls natively */
    onPillsPointerDown(event) {
      const pills = event.target.closest('.ce-pills');
      if (!pills || event.pointerType !== 'mouse' || event.button !== 0) return;
      if (pills.scrollWidth <= pills.clientWidth) return;

      const startX = event.clientX;
      const startScroll = pills.scrollLeft;
      this.pillsDragged = false;

      const onMove = (moveEvent) => {
        const distance = moveEvent.clientX - startX;
        if (!this.pillsDragged && Math.abs(distance) < 5) return;
        this.pillsDragged = true;
        pills.classList.add('is-dragging');
        pills.scrollLeft = startScroll - distance;
      };

      const onUp = () => {
        pills.classList.remove('is-dragging');
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    }

    onPopState() {
      this.render(window.location.search.slice(1), false);
    }

    applyForm(form) {
      if (!form) return;
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      this.render(params.toString());
    }

    async render(searchParams, updateHistory = true) {
      const requestId = ++this.requestId;
      const url = `${window.location.pathname}?section_id=${this.dataset.sectionId}${searchParams ? `&${searchParams}` : ''}`;

      if (updateHistory) {
        history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
      }

      this.classList.add('is-loading');

      try {
        let html = this.cache.get(url);
        if (!html) {
          const response = await fetch(url);
          html = await response.text();
          this.cache.set(url, html);
        }
        if (requestId !== this.requestId) return;

        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('collection-emojibator');
        if (!next) return;
        this.swapContent(next);
      } catch (error) {
        window.location.search = searchParams;
      } finally {
        if (requestId === this.requestId) this.classList.remove('is-loading');
      }
    }

    /* Replace everything, but keep open dropdowns and focus so desktop filtering
       doesn't feel like a page reload */
    swapContent(next) {
      const openIds = Array.from(this.querySelectorAll('.ce-dropdown[open]'), (details) => details.id);
      const focusedId = this.contains(document.activeElement) ? document.activeElement.id : null;

      this.innerHTML = next.innerHTML;

      openIds.forEach((id) => document.getElementById(id)?.setAttribute('open', ''));
      if (focusedId) document.getElementById(focusedId)?.focus();
    }

    debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define('collection-emojibator', CollectionEmojibator);
}
